import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './SkykitMonitor.css';

import Cookies from "universal-cookie";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
const cookies = new Cookies();
const token = cookies.get("token");
// Update this with your actual API base URL
const API_BASE_URL = process.env.REACT_APP_API || 'http://localhost:8000/jupiter';

const SkykitMonitor = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  // Fetch stats from API
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/workOrders/skykit-logs/stats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      );
      const data = await response.json();

      if (data.status === 'success') {
        setStats(data.data);
        setError(null);
      } else {
        setError('Failed to fetch stats');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Download logs
  const handleDownload = async () => {
    try {

      const response = await fetch(`${API_BASE_URL}/workOrders/skykit-logs/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // adjust based on your auth type
          // 'x-auth-token': token, // or if you use a different header
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get the blob data
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'skykit-logs.jsonl'; // or get filename from response headers
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      // Show error to user
      alert('Failed to download logs. Please try again.');
    }
  };

  // Clear logs
  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all logs?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/workOrders/skykit-logs`, {
        method: 'DELETE',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.status === 'success') {
        alert('Logs cleared successfully');
        fetchStats();
      }
    } catch (err) {
      alert('Failed to clear logs: ' + err.message);
    }
  };

  // Initial load
  useEffect(() => {
    fetchStats();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchStats();
      }, 30000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="skykit-monitor">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading Skykit stats...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skykit-monitor">
        <div className="error-container">
          <div className="error-message">
            <strong>⚠️ Error:</strong> {error}
          </div>
          <button className="btn btn-primary" onClick={fetchStats}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!stats || stats.totalCalls === 0) {
    return (
      <div className="skykit-monitor">
        <div className="header-sky">
          <h1>🚀 Skykit API Call Monitor</h1>
          <p>Real-time tracking of Skykit API calls and success rates</p>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>No API Calls Yet</h2>
          <p>Skykit API calls will appear here once they're made.</p>
          <button className="btn btn-primary" onClick={fetchStats}>
            🔄 Refresh
          </button>
        </div>
      </div>
    );
  }

  const successRate = stats.totalCalls > 0
    ? ((stats.successCalls / stats.totalCalls) * 100).toFixed(1)
    : 0;

  const peakMinute = stats.callsPerMinute.length > 0
    ? Math.max(...stats.callsPerMinute.map(m => m.total))
    : 0;

  // Chart data
  const chartData = {
    labels: stats.callsPerMinute.map(item => {
      const date = new Date(item.minute);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }),
    datasets: [
      {
        label: 'Success',
        data: stats.callsPerMinute.map(item => item.success),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
      {
        label: 'Failure',
        data: stats.callsPerMinute.map(item => item.failure),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    }
  };

  return (
    <div className="skykit-monitor">
      <div className="header-sky">
        <div className="header-content">
          <h1>🚀 Skykit API Call Monitor</h1>
          <p>Real-time tracking of Skykit API calls and success rates</p>
        </div>
        {stats.lastUpdated && (
          <div className="last-updated">
            <span className="pulse"></span>
            <span>Last updated: {new Date(stats.lastUpdated).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">📞</div>
          <div className="stat-content">
            <div className="stat-label">Total Calls</div>
            <div className="stat-value">{stats.totalCalls}</div>
            <div className="stat-subtitle">All API requests</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Success</div>
            <div className="stat-value">{stats.successCalls}</div>
            <div className="stat-subtitle">{successRate}% success rate</div>
          </div>
        </div>

        <div className="stat-card failure">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-label">Failures</div>
            <div className="stat-value">{stats.failureCalls}</div>
            <div className="stat-subtitle">{(100 - successRate).toFixed(1)}% failure rate</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-label">Peak Minute</div>
            <div className="stat-value">{peakMinute}</div>
            <div className="stat-subtitle">Highest calls/minute</div>
          </div>
        </div>
      </div>

      {stats.callsPerMinute && stats.callsPerMinute.length > 0 && (
        <div className="chart-container">
          <h2>📊 Calls Per Minute (Last 24 Hours)</h2>
          <div className="chart-wrapper">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {stats.callsPerMinute && stats.callsPerMinute.length > 0 && (
        <div className="table-container">
          <h2>📋 Detailed Breakdown</h2>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Total Calls</th>
                  <th>Success</th>
                  <th>Failures</th>
                  <th>Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.callsPerMinute.slice(-20).reverse().map((item, idx) => {
                  const rate = ((item.success / item.total) * 100).toFixed(0);
                  return (
                    <tr key={idx}>
                      <td>{new Date(item.minute).toLocaleString()}</td>
                      <td><strong>{item.total}</strong></td>
                      <td className="success-cell">{item.success}</td>
                      <td className="failure-cell">{item.failure}</td>
                      <td>
                        <span className="rate-badge">{rate}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="actions-container">
        <h2>⚙️ Actions</h2>
        <div className="actions">
          <button
            className="btn btn-primary"
            onClick={fetchStats}
          >
            🔄 Refresh Now
          </button>

          <button
            className="btn btn-success"
            onClick={handleDownload}
            disabled={stats.totalCalls === 0}
          >
            📥 Download Logs
          </button>

          <button
            className={`btn ${autoRefresh ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '⏸️ Stop Auto-Refresh' : '▶️ Start Auto-Refresh'}
          </button>

          <button
            className="btn btn-danger"
            onClick={handleClearLogs}
            disabled={stats.totalCalls === 0}
          >
            🗑️ Clear Logs
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkykitMonitor;
